import { create } from 'zustand';
import type { EditTab, UIState, ModalState } from '../types';

interface UIStoreState extends UIState {
  // Actions
  setCurrentEditTab: (tab: EditTab) => void;
  setLoading: (loading: boolean) => void;
  setIsSaving: (saving: boolean) => void;
  
  // Navigation lock actions
  lockNavigation: (reason?: string) => void;
  unlockNavigation: () => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  
  // Modal actions
  openModal: (modal: keyof ModalState) => void;
  closeModal: (modal: keyof ModalState) => void;
  closeAllModals: () => void;
  
  // Utility actions
  resetUI: () => void;
}

export const useUIStore = create<UIStoreState>((set, get) => ({
  // Initial state
  currentEditTab: 'settings',
  isLoading: false,
  isSaving: false,
  modals: {
    upsell: false,
    delete: false,
    confluence: false,
    navigationConfirm: false
  },
  navigationLock: {
    isLocked: false,
    lockReason: undefined,
    pendingNavigation: undefined
  },
  
  setCurrentEditTab: (tab) => set({ currentEditTab: tab }),
  
  setLoading: (loading) => set({ isLoading: loading }),
  
  setIsSaving: (saving) => set({ isSaving: saving }),
  
  // Navigation lock actions
  lockNavigation: (reason) => set({
    navigationLock: {
      isLocked: true,
      lockReason: reason,
      pendingNavigation: undefined
    }
  }),
  
  unlockNavigation: () => set({
    navigationLock: {
      isLocked: false,
      lockReason: undefined,
      pendingNavigation: undefined
    }
  }),
  
  confirmNavigation: () => {
    const state = get();
    const pendingNav = state.navigationLock.pendingNavigation;
    
    if (pendingNav) {
      // Unlock navigation - actual navigation is handled by NavigationConfirmModal
      set({
        navigationLock: {
          isLocked: false,
          lockReason: undefined,
          pendingNavigation: undefined
        },
        modals: {
          ...state.modals,
          navigationConfirm: false
        }
      });
    }
  },
  
  cancelNavigation: () => set((state) => ({
    navigationLock: {
      ...state.navigationLock,
      pendingNavigation: undefined
    },
    modals: {
      ...state.modals,
      navigationConfirm: false
    }
  })),
  
  // Modal actions
  openModal: (modal) => set((state) => ({
    modals: {
      ...state.modals,
      [modal]: true
    }
  })),
  
  closeModal: (modal) => set((state) => ({
    modals: {
      ...state.modals,
      [modal]: false
    }
  })),
  
  closeAllModals: () => set({
    modals: {
      upsell: false,
      delete: false,
      confluence: false,
      navigationConfirm: false
    }
  }),
  
  // Utility actions
  resetUI: () => set({
    currentEditTab: 'settings',
    isLoading: false,
    isSaving: false,
    modals: {
      upsell: false,
      delete: false,
      confluence: false,
      navigationConfirm: false
    },
    navigationLock: {
      isLocked: false,
      lockReason: undefined,
      pendingNavigation: undefined
    }
  })
})); 